import type { CreationTask } from '../api/creations'
import { getCreation, reconcileCreation } from '../api/creations'
import { createAsyncTaskHub } from './asyncTaskHub'
import {
  creationTaskAgeMs,
  DEFAULT_CREATION_RECOVERY_MAX_WAIT_MS,
  DEFAULT_CREATION_STALE_MS,
  isCreationResultPersistFailure,
  isCreationTaskTerminal,
  type RecoverCreationTaskOptions,
} from './creationTaskRecovery'
import { SSE_EVENT_CREATION_TASK_UPDATED } from './sseEventNames'

const creationTaskHub = createAsyncTaskHub<CreationTask>({
  path: '/v1/creations/events',
  eventName: SSE_EVENT_CREATION_TASK_UPDATED,
  getId: (task) => task.publicId,
})

/**
 * 按任务 id 订阅创作任务状态推送（SSE 观察者链：后端任务状态机 → SSE Hub → 本回调）。
 * 供智能体对话里的生图卡片等监听方使用；返回取消订阅函数。
 */
export function subscribeCreationTask(
  taskId: string,
  listener: (task: CreationTask) => void,
): () => void {
  return creationTaskHub.subscribe(taskId, listener)
}

async function loadCreationTask(taskId: string): Promise<CreationTask | null> {
  const trimmed = taskId.trim()
  if (!trimmed) return null
  try {
    return await getCreation(trimmed)
  } catch {
    return null
  }
}

/**
 * 等待创作任务进入终态：SSE 正常时纯推送，断线后 HTTP 轮询兜底。
 */
export async function awaitCreationTaskTerminal(
  initial: CreationTask,
  options?: { maxWaitMs?: number; pollMs?: number },
): Promise<CreationTask> {
  return creationTaskHub.waitForTerminal(initial, {
    isTerminal: isCreationTaskTerminal,
    load: getCreation,
    maxWaitMs: options?.maxWaitMs ?? DEFAULT_CREATION_RECOVERY_MAX_WAIT_MS,
    pollMs: options?.pollMs ?? 2000,
    timeoutMessage: '生成超时，请稍后在创作记录中查看该任务',
  })
}

/**
 * 创建后若后端异步生成：经统一生命周期模块等待（推送优先，断线查询兜底）。
 */
export async function waitForCreationTask(
  initial: CreationTask,
  options?: { intervalMs?: number; maxWaitMs?: number },
): Promise<CreationTask> {
  return awaitCreationTaskTerminal(initial, {
    maxWaitMs: options?.maxWaitMs,
    pollMs: options?.intervalMs,
  })
}

/**
 * 单任务对账：查库 → 已成功则同步 → 落库失败则补偿 → 进行中则等待终态。
 * 重进页面、下拉刷新、手动重试落库共用此入口。
 */
export async function recoverCreationRoundTask(
  taskId: string,
  options?: RecoverCreationTaskOptions,
): Promise<CreationTask | null> {
  const trimmed = taskId.trim()
  if (!trimmed) return null

  let task = await loadCreationTask(trimmed)
  if (!task) return null

  creationTaskHub.dispatch(task)

  if (task.status === 'SUCCEEDED') {
    return task
  }

  if (
    task.status === 'FAILED' &&
    isCreationResultPersistFailure(task) &&
    options?.reconcileStorageFailure !== false &&
    task.reconcilable !== false
  ) {
    try {
      task = await reconcileFailedCreationTask(trimmed, options)
    } catch {
      return loadCreationTask(trimmed)
    }
    if (!task) return null
  }

  if (isCreationTaskTerminal(task)) {
    return task
  }
  if (!options?.waitForCompletion) {
    return task
  }

  const staleMs = options.staleMs ?? DEFAULT_CREATION_STALE_MS
  if (creationTaskAgeMs(task, staleMs) > staleMs) {
    return task
  }

  try {
    return await awaitCreationTaskTerminal(task, {
      maxWaitMs: options.maxWaitMs ?? DEFAULT_CREATION_RECOVERY_MAX_WAIT_MS,
    })
  } catch {
    return loadCreationTask(trimmed)
  }
}

/**
 * 落库失败补偿：重新入队后可选等待终态（不重新调用上游生图）。
 */
export async function reconcileFailedCreationTask(
  taskId: string,
  options?: Pick<RecoverCreationTaskOptions, 'waitForCompletion' | 'maxWaitMs'>,
): Promise<CreationTask> {
  const trimmed = taskId.trim()
  if (!trimmed) {
    throw new Error('任务 ID 为空')
  }
  const task = await reconcileCreation(trimmed)
  creationTaskHub.dispatch(task)
  if (!options?.waitForCompletion || isCreationTaskTerminal(task)) {
    return task
  }
  return await awaitCreationTaskTerminal(task, {
    maxWaitMs: options?.maxWaitMs ?? DEFAULT_CREATION_RECOVERY_MAX_WAIT_MS,
  })
}

export type { RecoverCreationTaskOptions } from './creationTaskRecovery'
export { roundNeedsTaskRecovery } from './creationTaskRecovery'
